import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, Redirect, useHistory } from "react-router-dom";
import {
  FaBullhorn,
  FaExternalLinkAlt,
  FaFilePdf,
  FaHome,
  FaImage,
  FaLink,
  FaPlus,
  FaSyncAlt,
  FaTasks,
  FaUserGraduate,
  FaUserTie,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import BackButton from "../ChildComponent/BackButton";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import "../css/Admin_css/NoticeBoard.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

// Created By Cursor
function getAuthContext() {
  const schoolCode = SessionManager.getSchoolCode();
  const sessions = SessionManager.getCurrentSession();
  const sessionId = Array.isArray(sessions) ? sessions[0]?.session_id : "";
  const staff = SessionManager.getStaffDetails() || {};
  return {
    schoolCode,
    sessionId,
    staffId: staff.staff_id,
    loginType: staff.login_type,
  };
}

// Created By Cursor
function resolveMediaUrl(pathOrUrl) {
  const raw = String(pathOrUrl || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  const host = String(process.env.REACT_APP_SERVER_NAME || "").replace(/\/$/, "");
  if (!host) return raw;
  return `${host}/${raw.replace(/^\//, "")}`;
}

// Created By Cursor
function formatLinkLabel(url) {
  try {
    const u = new URL(url);
    const path = u.pathname === "/" ? "" : u.pathname;
    const label = `${u.host}${path}`;
    return label.length > 48 ? `${label.slice(0, 45)}…` : label;
  } catch (err) {
    return url.length > 48 ? `${url.slice(0, 45)}…` : url;
  }
}

// Created By Cursor
function NoticeCard({ item, index }) {
  const text = item?.notice || "—";
  const by = item?.ordered_by || "—";
  const date =
    item?.notice_date2 || item?.notice_date || item?.date_added || "";
  // Created By Cursor — resolve image/PDF from API fields saved in tm_notice_board
  const attachUrl = resolveMediaUrl(
    item?.attachment_url ||
      item?.attachment_path ||
      item?.Attachment_path ||
      item?.Attachment_url ||
      ""
  );
  const attachName =
    item?.attachment_name || item?.Attachment_name || "Attachment";
  const attachType = String(
    item?.attachment_type || item?.Attachment_type || ""
  ).toLowerCase();
  let noticeUrl = String(
    item?.notice_url || item?.Notice_url || item?.url || ""
  ).trim();
  if (noticeUrl && noticeUrl.toLowerCase() !== "null") {
    if (!/^https?:\/\//i.test(noticeUrl)) {
      noticeUrl = `https://${noticeUrl}`;
    }
  } else {
    noticeUrl = "";
  }
  const isImage =
    attachType === "image" ||
    attachType.startsWith("image/") ||
    /\.(jpe?g|png|gif|webp)$/i.test(attachName) ||
    /\.(jpe?g|png|gif|webp)$/i.test(attachUrl);
  const isPdf =
    attachType === "pdf" ||
    attachType === "application/pdf" ||
    /\.pdf$/i.test(attachName) ||
    /\.pdf$/i.test(attachUrl);
  const hasMedia = Boolean(attachUrl || noticeUrl);

  return (
    <article className={`nb-card${hasMedia ? " has-media" : ""}`}>
      <div className="nb-card__top">
        <div className="nb-card__index" aria-hidden>
          {index + 1}
        </div>
        <div className="nb-card__meta">
          <span className="nb-chip">{by}</span>
          {date ? <span className="nb-chip nb-chip--soft">{date}</span> : null}
          {attachUrl && isImage ? (
            <span className="nb-chip nb-chip--image">
              <FaImage aria-hidden /> Image
            </span>
          ) : null}
          {attachUrl && isPdf ? (
            <span className="nb-chip nb-chip--pdf">
              <FaFilePdf aria-hidden /> PDF
            </span>
          ) : null}
          {noticeUrl ? (
            <span className="nb-chip nb-chip--url">
              <FaLink aria-hidden /> Link
            </span>
          ) : null}
        </div>
      </div>

      <p className="nb-card__text">{text}</p>

      {hasMedia ? (
        <div className="nb-card__media">
          {attachUrl && isImage ? (
            <a
              className="nb-media-image"
              href={attachUrl}
              target="_blank"
              rel="noopener noreferrer"
              title={attachName}
            >
              <img src={attachUrl} alt={attachName} />
              <span className="nb-media-image__caption">
                <FaImage aria-hidden />
                {attachName}
              </span>
            </a>
          ) : null}

          {attachUrl && !isImage ? (
            <a
              className={`nb-media-file${isPdf ? " is-pdf" : ""}`}
              href={attachUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="nb-media-file__icon" aria-hidden>
                {isPdf ? <FaFilePdf /> : <FaExternalLinkAlt />}
              </span>
              <span className="nb-media-file__text">
                <strong>{attachName}</strong>
                <small>{isPdf ? "Open PDF" : "Open attachment"}</small>
              </span>
              <FaExternalLinkAlt className="nb-media-file__go" aria-hidden />
            </a>
          ) : null}

          {noticeUrl ? (
            <a
              className="nb-media-file is-url"
              href={noticeUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="nb-media-file__icon" aria-hidden>
                <FaLink />
              </span>
              <span className="nb-media-file__text">
                <strong>Related link</strong>
                <small>{formatLinkLabel(noticeUrl)}</small>
              </span>
              <FaExternalLinkAlt className="nb-media-file__go" aria-hidden />
            </a>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

// Created By Cursor
export default function NoticeBoard() {
  const history = useHistory();
  const loggedIn = localStorage.getItem("token") != null;

  const [loading, setLoading] = useState(false);
  const [noticeBoard, setNoticeBoard] = useState([]);
  const [staffNoticeBoard, setStaffNoticeBoard] = useState([]);
  const [activeTab, setActiveTab] = useState("student");

  const loadNotices = useCallback(async () => {
    const { schoolCode, sessionId, staffId, loginType } = getAuthContext();
    if (!schoolCode || !sessionId) {
      myToast.notify_warning("Missing school or session details.");
      setNoticeBoard([]);
      setStaffNoticeBoard([]);
      return;
    }

    setLoading(true);
    const params = {
      school_code: schoolCode,
      session_id: sessionId,
      staff_id: staffId,
      login_type: loginType,
    };

    try {
      const [studentRes, staffRes] = await Promise.all([
        axios
          .get(`${API_BASE()}getStudentNoticeBoard.php`, { params })
          .then((res) => res.data),
        axios
          .get(`${API_BASE()}getStaffNoticeBoard.php`, { params })
          .then((res) => res.data),
      ]);

      if (studentRes?.api_status === "OK") {
        setNoticeBoard(Array.isArray(studentRes.data) ? studentRes.data : []);
      } else {
        setNoticeBoard([]);
        if (studentRes?.api_remark) {
          myToast.notify_warning(studentRes.api_remark);
        }
      }

      if (staffRes?.api_status === "OK") {
        setStaffNoticeBoard(
          Array.isArray(staffRes.data) ? staffRes.data : []
        );
      } else {
        setStaffNoticeBoard([]);
        if (staffRes?.api_remark) {
          myToast.notify_warning(staffRes.api_remark);
        }
      }
    } catch (error) {
      console.error(error);
      setNoticeBoard([]);
      setStaffNoticeBoard([]);
      myToast.notify_warning("Failed to load notices.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotices();
  }, [loadNotices]);

  const activeList = useMemo(
    () => (activeTab === "student" ? noticeBoard : staffNoticeBoard),
    [activeTab, noticeBoard, staffNoticeBoard]
  );

  if (!loggedIn) {
    return <Redirect to="/Login" />;
  }

  return (
    <div
      className="content payroll-page sm-page nb-page"
      style={{ fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header d-flex justify-content-between align-items-center"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header d-flex align-items-center gap-2">
          <button
            type="button"
            className="qb-home-btn"
            aria-label="Home"
            onClick={() => history.push("/AdminMain")}
          >
            <FaHome />
          </button>
          <ul className="breadcrumbs mb-0">
            <li>
              <button
                type="button"
                className="nb-crumb-link"
                onClick={() => history.push("/AdminMain/ActivityManagement")}
              >
                Activity Management
              </button>
            </li>
            <li>
              <span style={{ color: COLORS.white }}>Notice Board</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell nb-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaBullhorn />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Notice Board</h1>
              <p className="sm-hero__sub">
                {`${noticeBoard.length} student · ${staffNoticeBoard.length} staff notice${
                  staffNoticeBoard.length === 1 ? "" : "s"
                }`}
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <Link to="/AdminMain/ManageNotice" className="nb-hero-btn">
              <FaTasks aria-hidden /> Manage
            </Link>
            <Link
              to="/AdminMain/AddNotice"
              className="nb-hero-btn nb-hero-btn--solid"
            >
              <FaPlus aria-hidden /> Add
            </Link>
            <button
              type="button"
              className={`pp-icon-btn${loading ? " spinning" : ""}`}
              onClick={loadNotices}
              disabled={loading}
              aria-label="Refresh"
              title="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
          </div>
        </header>

        <div className="nb-body">
          <div className="nb-tabs" role="tablist" aria-label="Notice type">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "student"}
              className={`nb-tab${activeTab === "student" ? " is-active" : ""}`}
              onClick={() => setActiveTab("student")}
            >
              <FaUserGraduate aria-hidden />
              Student
              <em>{noticeBoard.length}</em>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "staff"}
              className={`nb-tab${activeTab === "staff" ? " is-active" : ""}`}
              onClick={() => setActiveTab("staff")}
            >
              <FaUserTie aria-hidden />
              Staff
              <em>{staffNoticeBoard.length}</em>
            </button>
          </div>

          {loading && !activeList.length ? (
            <div className="nb-loading">
              <MyLoader />
            </div>
          ) : null}

          {!loading && !activeList.length ? (
            <div className="nb-empty">
              <div className="nb-empty__circle" aria-hidden>
                <FaBullhorn />
              </div>
              <p className="nb-empty__title">No notices available</p>
              <p className="nb-empty__hint">
                {activeTab === "student"
                  ? "No student notices for this session."
                  : "No staff notices for this session."}
              </p>
              <Link to="/AdminMain/AddNotice" className="nb-empty__cta">
                <FaPlus aria-hidden /> Add Notice
              </Link>
            </div>
          ) : null}

          {activeList.length ? (
            <div className="nb-list">
              {activeList.map((item, index) => (
                <NoticeCard
                  key={item.notice_id || `${activeTab}-${index}`}
                  item={item}
                  index={index}
                />
              ))}
            </div>
          ) : null}

          <div className="nb-actions">
            <Link
              to="/AdminMain/ManageNotice"
              className="nb-btn nb-btn--secondary"
            >
              <FaTasks aria-hidden /> Manage Notices
            </Link>
            <Link to="/AdminMain/AddNotice" className="nb-btn nb-btn--primary">
              <FaPlus aria-hidden /> Add Notice
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
